import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Building, Save, Users, ExternalLink, Loader2, Edit3, Sparkles } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { useAppSelector } from '@/hooks/useAppSelector';
import { useToast } from '@/hooks/use-toast';
import { Company, CompanyUpdate, Category } from '@/hooks/useManagementAPI';
import { invokeEdgeFunction } from '@/utils/edgeFunctionUtils';
import { uploadToCloudinary } from '@/utils/cloudinaryUpload';

interface CompanyHeroSectionProps {
  company: Company;
  categories: Category[];
  loading?: boolean;
  onUpdate: (updates: CompanyUpdate) => Promise<Company | null>;
  onUpdateSuccess?: () => void;
  onUpdateError?: (error: string) => void;
}

export const CompanyHeroSection: React.FC<CompanyHeroSectionProps> = ({
  company,
  categories,
  loading = false,
  onUpdate,
  onUpdateSuccess,
  onUpdateError,
}) => {
  const { toast } = useToast();
  const [isSaving, setIsSaving] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);

  // Edited state - use category_id for category
  const [editedDescription, setEditedDescription] = useState(company.description || '');
  const [editedCategoryId, setEditedCategoryId] = useState(company.category_id || '');
  const [editedEmployees, setEditedEmployees] = useState(company.employees || '');
  const [editedLogoUrl, setEditedLogoUrl] = useState(company.logo_url || '');
  const [editedUrl, setEditedUrl] = useState(company.url || '');

  // Which field is being edited
  const [editingField, setEditingField] = useState<string | null>(null);

  // Get unique employees from Redux store (employees aren't in Vault API categories)
  const uniqueEmployees = useAppSelector(state =>
    state.supaCompanies.uniqueEmployees.filter(emp => emp !== 'All')
  );

  // Create category lookup map
  const categoryMap = useMemo(() => {
    const map = new Map<string, string>();
    categories.forEach(cat => map.set(cat.id, cat.name));
    return map;
  }, [categories]);

  // Get category name from ID
  const getCategoryName = (categoryId: string | null): string => {
    if (!categoryId) return '';
    return categoryMap.get(categoryId) || '';
  };

  // Format company name from slug (with safety check)
  const companyName = company.slug
    ? company.slug
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    : company.name || 'Unknown Company';

  // Sync state when company prop changes
  useEffect(() => {
    setEditedDescription(company.description || '');
    setEditedCategoryId(company.category_id || '');
    setEditedEmployees(company.employees || '');
    setEditedLogoUrl(company.logo_url || '');
    setEditedUrl(company.url || '');
  }, [company]);

  // Track if there are unsaved changes
  const hasChanges =
    editedDescription !== (company.description || '') ||
    editedCategoryId !== (company.category_id || '') ||
    editedEmployees !== (company.employees || '') ||
    editedLogoUrl !== (company.logo_url || '') ||
    editedUrl !== (company.url || '');

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const updates: CompanyUpdate = {};

      if (editedDescription !== (company.description || '')) {
        updates.description = editedDescription;
      }
      if (editedCategoryId !== (company.category_id || '')) {
        updates.category_id = editedCategoryId;
      }
      if (editedEmployees !== (company.employees || '')) {
        updates.employees = editedEmployees;
      }
      if (editedLogoUrl !== (company.logo_url || '')) {
        updates.logo_url = editedLogoUrl;
      }
      if (editedUrl !== (company.url || '')) {
        updates.url = editedUrl;
      }

      if (Object.keys(updates).length > 0) {
        await onUpdate(updates);
        onUpdateSuccess?.();
      }
    } catch (error) {
      console.error('[CompanyHeroSection] Save error:', error);
      onUpdateError?.(error instanceof Error ? error.message : 'Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  };

  // AI generation for description using web search
  const handleAIGenerate = async () => {
    setAiGenerating(true);
    try {
      const { data: aiData, error: aiError } = await invokeEdgeFunction(
        'ai-composer',
        {
          systemPrompt: `You write concise company profiles in a specific format. Be factual and use web search to find accurate information.`,
          userPrompt: `Search for "${companyName}" company and write a brief profile with these elements in order:

1. "[City, State]-based [industry] company founded [year]."
2. "[Private company / Public company (TICKER: XXX)]."
3. "Operates in [main business areas/industries]."
4. "Serves [target customers] with [main products/services]."

Example output:
"Mountain View, CA-based tech company founded 1998. Public company (NASDAQ: GOOGL). Operates in cloud computing, search, software, and hardware. Serves consumers and businesses globally with cloud infrastructure, productivity tools, and enterprise solutions."

Another example:
"San Francisco, CA-based fintech company founded 2010. Private company. Operates in payment processing and financial infrastructure. Serves businesses of all sizes with payment APIs, billing tools, and fraud prevention."

Write 3-4 sentences maximum. Be concise.`,
          maxTokens: 200,
          temperature: 0.2,
          useGemini: true,
          enableGrounding: true,
        }
      );

      if (aiError) {
        throw new Error(aiError.message || 'AI generation failed');
      }

      setEditedDescription(aiData.text);

      toast({
        title: "AI Generated",
        description: "Description generated with AI. Click Save to persist.",
      });
    } catch (error: unknown) {
      console.error('[CompanyHeroSection] AI generation error:', error);
      const errorMessage = error instanceof Error ? error.message : "Failed to generate description with AI";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setAiGenerating(false);
    }
  };

  if (loading) {
    return <CompanyHeroSkeleton />;
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumbs */}
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/companies">Companies</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>{companyName}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      {/* Hero Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 relative">
        {/* Save button - top right */}
        <div className="absolute top-4 right-4 flex items-center gap-2">
          {hasChanges && (
            <span className="text-sm text-amber-600">Unsaved changes</span>
          )}
          <Button
            onClick={handleSave}
            disabled={!hasChanges || isSaving}
            size="sm"
          >
            {isSaving ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-1" />
            )}
            Save
          </Button>
        </div>

        <div className="flex gap-6">
          {/* Logo */}
          <div className="flex-shrink-0">
            {editingField === 'logo' ? (
              <div className="space-y-2">
                <Input
                  value={editedLogoUrl}
                  onChange={(e) => setEditedLogoUrl(e.target.value)}
                  onBlur={() => setEditingField(null)}
                  placeholder="https://example.com/logo.png"
                  className="w-48"
                  autoFocus
                />
                {editedLogoUrl && (
                  <img
                    src={editedLogoUrl}
                    alt="Logo preview"
                    className="w-20 h-20 object-contain rounded-lg border border-gray-100"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                )}
              </div>
            ) : (
              <div
                className="cursor-pointer group relative"
                onClick={() => setEditingField('logo')}
              >
                {company.logo_url || editedLogoUrl ? (
                  <img
                    src={editedLogoUrl || company.logo_url || ''}
                    alt={`${companyName} logo`}
                    className="w-20 h-20 object-contain rounded-lg border border-gray-100"
                  />
                ) : (
                  <div className="w-20 h-20 bg-gray-100 rounded-lg flex items-center justify-center">
                    <Building className="w-10 h-10 text-gray-400" />
                  </div>
                )}
                <div className="absolute inset-0 bg-black/50 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <Edit3 className="h-5 w-5 text-white" />
                </div>
              </div>
            )}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0 pr-24">
            {/* Company Name */}
            <h1 className="text-2xl font-bold text-gray-900">{companyName}</h1>

            {/* Description - click to edit */}
            {editingField === 'description' ? (
              <div className="mt-2 pr-8 relative">
                <Textarea
                  value={editedDescription}
                  onChange={(e) => setEditedDescription(e.target.value)}
                  onBlur={() => !aiGenerating && setEditingField(null)}
                  placeholder="Enter company description..."
                  rows={3}
                  className="resize-none border-blue-300 focus:border-blue-500 w-full"
                  autoFocus
                />
                {/* Show spinner while AI is generating in edit mode */}
                {aiGenerating && (
                  <div className="absolute top-2 right-2">
                    <Loader2 className="h-4 w-4 text-purple-500 animate-spin" />
                  </div>
                )}
              </div>
            ) : (
              <div
                className="mt-2 cursor-pointer group relative"
                onClick={() => setEditingField('description')}
              >
                <p className="text-gray-600 pr-16">
                  {editedDescription || <span className="text-gray-300 italic">Click to add description...</span>}
                </p>
                {/* Show buttons on hover OR always when AI is generating */}
                <div className={`absolute top-0 right-0 flex items-center gap-1 transition-opacity ${aiGenerating ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAIGenerate();
                    }}
                    disabled={aiGenerating}
                    title="Generate with AI"
                  >
                    {aiGenerating ? (
                      <Loader2 className="h-3.5 w-3.5 text-purple-500 animate-spin" />
                    ) : (
                      <Sparkles className="h-3.5 w-3.5 text-purple-500" />
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingField('description');
                    }}
                    title="Edit"
                  >
                    <Edit3 className="h-3.5 w-3.5 text-gray-400" />
                  </Button>
                </div>
              </div>
            )}

            {/* Badges row */}
            <div className="flex flex-wrap items-center gap-3 mt-4">
              {/* Category - click to edit */}
              {editingField === 'category' ? (
                <Select
                  value={editedCategoryId}
                  onValueChange={(value) => {
                    setEditedCategoryId(value);
                    setEditingField(null);
                  }}
                  open={true}
                  onOpenChange={(open) => !open && setEditingField(null)}
                >
                  <SelectTrigger className="w-64">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Badge
                  variant="secondary"
                  className="bg-blue-50 text-blue-700 border-blue-200 cursor-pointer hover:bg-blue-100"
                  onClick={() => setEditingField('category')}
                >
                  {getCategoryName(editedCategoryId) || <span className="italic">Add category</span>}
                </Badge>
              )}

              {/* Employees - click to edit */}
              {editingField === 'employees' ? (
                <Select
                  value={editedEmployees}
                  onValueChange={(value) => {
                    setEditedEmployees(value);
                    setEditingField(null);
                  }}
                  open={true}
                  onOpenChange={(open) => !open && setEditingField(null)}
                >
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Select employee range" />
                  </SelectTrigger>
                  <SelectContent>
                    {uniqueEmployees.map((emp) => (
                      <SelectItem key={emp} value={emp}>
                        {emp}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Badge
                  variant="secondary"
                  className="bg-green-50 text-green-700 border-green-200 cursor-pointer hover:bg-green-100"
                  onClick={() => setEditingField('employees')}
                >
                  <Users className="w-3 h-3 mr-1" />
                  {editedEmployees ? `${editedEmployees} employees` : <span className="italic">Add size</span>}
                </Badge>
              )}

              {/* URL - click to edit */}
              {editingField === 'url' ? (
                <Input
                  value={editedUrl}
                  onChange={(e) => setEditedUrl(e.target.value)}
                  onBlur={() => setEditingField(null)}
                  placeholder="https://example.com"
                  className="w-64"
                  autoFocus
                />
              ) : editedUrl ? (
                <a
                  href={editedUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
                  onClick={(e) => e.stopPropagation()}
                >
                  <ExternalLink className="w-3 h-3 mr-1" />
                  Visit website
                </a>
              ) : (
                <span
                  className="inline-flex items-center text-sm text-gray-400 cursor-pointer hover:text-gray-600"
                  onClick={() => setEditingField('url')}
                >
                  <ExternalLink className="w-3 h-3 mr-1" />
                  <span className="italic">Add website</span>
                </span>
              )}
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

// Loading Skeleton
export const CompanyHeroSkeleton: React.FC = () => {
  return (
    <div className="space-y-6">
      {/* Breadcrumbs skeleton */}
      <div className="flex items-center gap-2">
        <Skeleton className="h-4 w-20" />
        <Skeleton className="h-4 w-4" />
        <Skeleton className="h-4 w-24" />
      </div>

      {/* Hero card skeleton */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex gap-6">
          <Skeleton className="w-20 h-20 rounded-lg flex-shrink-0" />
          <div className="flex-1 space-y-4">
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-4 w-full max-w-xl" />
            <Skeleton className="h-4 w-3/4 max-w-lg" />
            <div className="flex gap-2 pt-2">
              <Skeleton className="h-6 w-24 rounded-full" />
              <Skeleton className="h-6 w-32 rounded-full" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyHeroSection;
